import { useLayoutEffect, type RefObject } from 'react';

/*
 * Reveals `[data-reveal]` blocks as they scroll into view (styles in landing.css).
 *
 * Hidden-until-seen content is only safe if nothing can stay hidden, so this does not
 * rely on IntersectionObserver alone: a jump (anchor link, fast flick, restored scroll
 * position) can carry a block from below the viewport to above it without it ever
 * "intersecting". Instead every scroll/resize frame sweeps the pending blocks and
 * reveals each one whose top has passed the reveal line, including everything already
 * above the viewport.
 *
 * Nothing is hidden unless this runs: the hidden state is keyed on a class added here,
 * so no JS or reduced motion means a fully visible page.
 */

const REVEAL_CLASS = 'reveal-on';
/** A block reveals once its top edge is above this fraction of the viewport height. */
const REVEAL_LINE = 0.9;
/** Blocks already on screen at load wait for the hero's entrance to get going. */
const LOAD_DELAY = '420ms';

export function useScrollReveal(rootRef: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let pending = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]:not(.is-in)'));
    if (pending.length === 0) return;

    const html = document.documentElement;
    html.classList.add(REVEAL_CLASS);

    let frame = 0;
    let firstSweep = true;

    const detach = () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };

    const sweep = () => {
      frame = 0;
      const line = window.innerHeight * REVEAL_LINE;
      // Read every position first, then write, so the sweep never forces layout per block.
      const tops = pending.map((el) => el.getBoundingClientRect().top);
      const stillHidden: HTMLElement[] = [];
      pending.forEach((el, i) => {
        if (tops[i] >= line) {
          stillHidden.push(el);
          return;
        }
        if (firstSweep) el.style.setProperty('--reveal-after', LOAD_DELAY);
        el.classList.add('is-in');
      });
      firstSweep = false;
      pending = stillHidden;
      if (pending.length === 0) detach();
    };

    function schedule() {
      if (!frame) frame = requestAnimationFrame(sweep);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // The first sweep runs on the next frame, after the hidden state has painted, so the
    // blocks that are already on screen animate in instead of popping.
    schedule();

    return () => {
      cancelAnimationFrame(frame);
      detach();
      html.classList.remove(REVEAL_CLASS);
    };
  }, [rootRef]);
}
